'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Filter, Download, RefreshCw } from 'lucide-react';
import CustomerTable from './CustomerTable';
import CustomerDetailModal from './CustomerDetailModal';
import AddCustomerModal from './AddCustomerModal';
import { Customer } from './customerTypes';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

export default function CustomerManagementContent() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const supabase = createClient();

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        toast.error('Failed to load customers');
        return;
      }

      const mapped: Customer[] = (data || []).map((row) => ({
        id: row.id,
        customerNumber: row.customer_number,
        fullName: row.full_name,
        email: row.email,
        mobile: row.mobile,
        address: row.address,
        landmark: row.landmark || '',
        machineModel: row.machine_model,
        monthlyFee: Number(row.monthly_fee),
        serviceStartDate: row.service_start_date || '',
        monthsActive: row.months_active,
        contractStatus: row.contract_status as Customer['contractStatus'],
        lockInMonths: row.lock_in_months,
        assignedTechnician: row.assigned_technician_name || '',
        notes: row.notes || '',
      }));
      setCustomers(mapped);
    } catch (err) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const filtered = customers.filter((c) => {
    const matchSearch =
      search === '' ||
      c.customerNumber.toLowerCase().includes(search.toLowerCase()) ||
      c.fullName.toLowerCase().includes(search.toLowerCase()) ||
      c.mobile.includes(search) ||
      c.machineModel.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || c.contractStatus === statusFilter;
    return matchSearch && matchStatus;
  });

  async function handleAddCustomer(c: Customer) {
    // Insert into Supabase
    const { error } = await supabase.from('customers').insert({
      customer_number: c.customerNumber,
      full_name: c.fullName,
      email: c.email,
      mobile: c.mobile,
      address: c.address,
      landmark: c.landmark,
      machine_model: c.machineModel,
      monthly_fee: c.monthlyFee,
      service_start_date: c.serviceStartDate || null,
      months_active: 0,
      contract_status: 'active',
      lock_in_months: 0,
      assigned_technician_name: c.assignedTechnician,
      notes: c.notes,
    });

    if (error) {
      toast.error('Failed to register customer: ' + error.message);
      return;
    }
    toast.success(`Customer ${c.customerNumber} registered successfully`);
    setAddModalOpen(false);
    fetchCustomers();
  }

  async function handleExportCSV() {
    const headers = ['Customer Number', 'Full Name', 'Email', 'Mobile', 'Machine Model', 'Monthly Fee', 'Status', 'Months Active'];
    const rows = customers.map((c) => [
      c.customerNumber, c.fullName, c.email, c.mobile, c.machineModel,
      c.monthlyFee, c.contractStatus, c.monthsActive,
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leenistech-customers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="px-6 py-5 max-w-screen-2xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-700 text-foreground">Customer Management</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {loading ? 'Loading...' : `${customers.length} registered customers — CUST number auto-assigned on registration`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-600 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
          >
            <Download size={15} />
            Export CSV
          </button>
          <button
            onClick={() => setAddModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-600 btn-primary rounded-lg"
          >
            <Plus size={16} />
            Register Customer
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by name, CUST number, mobile, machine model..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-600">Status:</span>
            {['all', 'active', 'suspended', 'expired'].map((s) => (
              <button
                key={`sf-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-colors ${
                  statusFilter === s
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
          <button
            onClick={fetchCustomers}
            className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bg-card rounded-xl border border-border p-8 text-center text-sm text-muted-foreground">
          Loading customers...
        </div>
      ) : (
        <CustomerTable
          customers={filtered}
          onViewCustomer={setSelectedCustomer}
        />
      )}

      {/* Detail Modal */}
      {selectedCustomer && (
        <CustomerDetailModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {/* Add Modal */}
      <AddCustomerModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={handleAddCustomer}
        nextNumber={customers.length + 1}
      />
    </div>
  );
}